import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Copy, Trash2, Check, Key, Loader2, MoreHorizontal } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import EmptyState from 'components/ui/EmptyState';
import { DatePicker } from 'components/ui/date-picker';
import { Label } from 'components/ui/label';
import Badge from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui';
import Modal from 'components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from 'components/ui/table';
import {
    DataTableToolbar,
    TableSearch,
    SegmentedTabs,
    ColumnsToggle,
    TableCount,
    RefChip,
    DataTablePagination,
} from 'components/ui/data-table';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import { useToast } from 'components/ui/use-toast';
import * as Invite from 'modules/invite';
import * as Test from 'modules/test';
import { useAuthContext } from 'modules/auth';
import type { PaginatedMeta } from '@examing/types';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from 'modules/auth/permissions';
interface InviteItem {
    id: string;
    code: string;
    expiresAt?: string;
    usedAt?: string;
    createdAt: string;
    testId: string;
    organizationId: string;
    candidateId?: string;
    test?: {
        id: string;
        title: string;
    } | null;
    candidate?: {
        id: string;
        name: string;
        email: string;
    } | null;
}
interface TestOption {
    id: string;
    title: string;
}
type InviteStatus = 'active' | 'used' | 'expired';
const getInviteStatus = (invite: InviteItem): InviteStatus => {
    if (invite.usedAt)
        return 'used';
    if (invite.expiresAt && new Date(invite.expiresAt) < new Date())
        return 'expired';
    return 'active';
};
const statusBadgeVariant = (status: string): 'success' | 'primary' | 'secondary' => {
    switch (status) {
        case 'active': return 'success';
        case 'used': return 'primary';
        default: return 'secondary';
    }
};
const formatDate = (dateStr?: string) => {
    if (!dateStr)
        return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};
const InviteManager: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state: authState } = useAuthContext();
    const { toast } = useToast();
    const orgId = authState.organization?.id;
    // Permission guard
    useEffect(() => {
        if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.ACCESS_KEYS_CREATE))
            navigate('/app');
    }, [authState.isFetched, authState.permissions, navigate]);
    // Invites state
    const [invites, setInvites] = useState<InviteItem[]>([]);
    const [meta, setMeta] = useState<PaginatedMeta>({ total: 0, page: 1, limit: 20, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    // Filters
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | InviteStatus>('all');
    const [testFilter, setTestFilter] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [cols, setCols] = useState({ assessment: true, status: true, created: true, expires: true, usedBy: true });
    // Tests for dropdown
    const [tests, setTests] = useState<TestOption[]>([]);
    // Generate modal
    const [showModal, setShowModal] = useState(false);
    const [genTestId, setGenTestId] = useState('');
    const [genCount, setGenCount] = useState(10);
    const [genExpiresAt, setGenExpiresAt] = useState('');
    const [generating, setGenerating] = useState(false);
    // Copy & delete
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);
    // Reset page on filter change
    useEffect(() => {
        setPage(1);
    }, [statusFilter, testFilter, perPage]);
    // Fetch tests for dropdown
    useEffect(() => {
        if (!orgId)
            return;
        Test.Api.List({ orgId, perPage: 100 } as any)
            .then((res: any) => {
            const d = res.data;
            const raw = Array.isArray(d)
                ? d
                : Array.isArray(d?.items)
                    ? d.items
                    : Array.isArray(d?.data)
                        ? d.data
                        : Array.isArray(d?.data?.items)
                            ? d.data.items
                            : [];
            const options: TestOption[] = raw
                .filter((t: any) => t && (t.id || t._id) && t.title)
                .map((t: any) => ({ id: t.id || t._id, title: t.title }));
            setTests(options);
        })
            .catch(() => {
            setTests([]);
        });
    }, [orgId]);
    // Fetch invites
    const fetchInvites = useCallback(async () => {
        if (!orgId)
            return;
        setLoading(true);
        try {
            const res = await Invite.Api.GetByOrganization(orgId, {
                search: debouncedSearch || undefined,
                status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
                testId: testFilter && testFilter !== 'all' ? testFilter : undefined,
                page,
                limit: perPage,
            }) as any;
            setInvites(res.data?.data || []);
            setMeta(res.data?.meta || { total: 0, page: 1, limit: perPage, totalPages: 0 });
        }
        catch {
            toast.error(t("failed_to_load_access_keys"));
        }
        finally {
            setLoading(false);
        }
    }, [orgId, debouncedSearch, statusFilter, testFilter, page, perPage, toast, t]);
    useEffect(() => {
        fetchInvites();
    }, [fetchInvites]);
    // Compute stats from meta/invites
    const stats = {
        active: invites.filter((i) => getInviteStatus(i) === 'active').length,
        used: invites.filter((i) => getInviteStatus(i) === 'used').length,
        expired: invites.filter((i) => getInviteStatus(i) === 'expired').length,
    };
    const handleCopy = (code: string, id: string) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };
    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await Invite.Api.Delete(id);
            toast.success(t("access_key_deleted"));
            fetchInvites();
        }
        catch (err: any) {
            const msg = await err?.response?.json?.().catch(() => null);
            toast.error(msg?.message || 'Failed to delete access key');
        }
        finally {
            setDeletingId(null);
        }
    };
    const handleGenerate = async () => {
        if (!genTestId) {
            toast.error(t("please_select_an_assessment"));
            return;
        }
        if (genCount < 1 || genCount > 100) {
            toast.error(t("number_of_codes_must_be_between_1_and_100"));
            return;
        }
        setGenerating(true);
        try {
            await Invite.Api.Generate(genTestId, genCount, genExpiresAt || undefined);
            toast.success(`${genCount} access key${genCount > 1 ? 's' : ''} generated successfully`);
            setShowModal(false);
            setGenTestId('');
            setGenCount(10);
            setGenExpiresAt('');
            fetchInvites();
        }
        catch (err: any) {
            const msg = await err?.response?.json?.().catch(() => null);
            toast.error(msg?.message || 'Failed to generate codes');
        }
        finally {
            setGenerating(false);
        }
    };
    return (<div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-2xl font-bold text-foreground tracking-tight sm:text-3xl">{t("access_keys")}</motion.h1>
          <p className="text-muted-foreground mt-1">{t("generate_and_manage_invite_codes_for_your_assessments")}</p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => setShowModal(true)}>
          <Plus size={16} className="mr-2"/>{t("generate_codes")}</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-2xl font-bold text-foreground">{meta.total}</p>
            <p className="text-xs font-medium text-muted-foreground mt-1">{t("total_codes")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active}</p>
            <p className="text-xs font-medium text-muted-foreground mt-1">{t("active_on_page")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-2xl font-bold text-muted-foreground">{stats.used + stats.expired}</p>
            <p className="text-xs font-medium text-muted-foreground mt-1">{t("used_expired_on_page")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <DataTableToolbar>
        <TableSearch value={search} onChange={setSearch} placeholder={t("search_by_code")} aria-label={t("search_by_code")}/>
        <ColumnsToggle
          columns={[
            { id: 'assessment', label: t("assessment"), visible: cols.assessment },
            { id: 'status', label: t("status"), visible: cols.status },
            { id: 'created', label: t("created"), visible: cols.created },
            { id: 'expires', label: t("expires"), visible: cols.expires },
            { id: 'usedBy', label: t("used_by"), visible: cols.usedBy },
          ]}
          onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
        />
        <SegmentedTabs<'all' | InviteStatus>
          aria-label={t("status")}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: t("all") },
            { value: 'active', label: t("active") },
            { value: 'used', label: t("used") },
            { value: 'expired', label: t("expired") },
          ]}
        />
        <Select value={testFilter} onValueChange={setTestFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t("all_assessments")}/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_assessments")}</SelectItem>
            {tests.map((t) => (<SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>))}
          </SelectContent>
        </Select>
        <TableCount>{meta.total} {t("items")}</TableCount>
      </DataTableToolbar>

      {/* Table */}
      {loading ? (<div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-muted-foreground"/>
          </div>) : invites.length === 0 ? (
            <EmptyState
              icon={<Key size={32} />}
              title={t("no_access_keys_found")}
              description={debouncedSearch || statusFilter !== 'all' || testFilter
                ? t("try_adjusting_your_search_or_filters")
                : t("generate_your_first_access_keys")}
              action={!debouncedSearch && statusFilter === 'all' && !testFilter
                ? { label: t("generate_codes"), onClick: () => setShowModal(true) }
                : undefined}
            />
          ) : (<>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("code")}</TableHead>
                  {cols.assessment && <TableHead>{t("assessment")}</TableHead>}
                  {cols.status && <TableHead>{t("status")}</TableHead>}
                  {cols.created && <TableHead className="hidden md:table-cell">{t("created")}</TableHead>}
                  {cols.expires && <TableHead className="hidden lg:table-cell">{t("expires")}</TableHead>}
                  {cols.usedBy && <TableHead className="hidden lg:table-cell">{t("used_by")}</TableHead>}
                  <TableHead className="text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => {
                const status = getInviteStatus(invite);
                return (<TableRow key={invite.id}>
                      <TableCell>
                        <RefChip>{invite.code}</RefChip>
                      </TableCell>
                      {cols.assessment && (<TableCell>
                        <span className="font-medium text-foreground">
                          {invite.test?.title || '—'}
                        </span>
                      </TableCell>)}
                      {cols.status && (<TableCell>
                        <Badge variant={statusBadgeVariant(status)} size="sm">
                          {t(status)}
                        </Badge>
                      </TableCell>)}
                      {cols.created && (<TableCell className="hidden text-muted-foreground text-sm md:table-cell">
                        {formatDate(invite.createdAt)}
                      </TableCell>)}
                      {cols.expires && (<TableCell className="hidden text-muted-foreground text-sm lg:table-cell">
                        {formatDate(invite.expiresAt)}
                      </TableCell>)}
                      {cols.usedBy && (<TableCell className="hidden lg:table-cell">
                        {invite.candidate ? (<div>
                            <span className="text-foreground text-sm">{invite.candidate.name}</span>
                            <p className="text-xs text-muted-foreground">{invite.candidate.email}</p>
                          </div>) : (<span className="text-muted-foreground">—</span>)}
                      </TableCell>)}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4"/>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => handleCopy(invite.code, invite.id)}>
                              {copiedId === invite.id
                            ? <Check className="h-4 w-4 mr-2 text-green-600"/>
                            : <Copy className="h-4 w-4 mr-2"/>}
                              {t("copy_code")}
                            </DropdownMenuItem>
                            {status === 'active' && (<DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => handleDelete(invite.id)} disabled={deletingId === invite.id}>
                                {deletingId === invite.id
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                : <Trash2 className="h-4 w-4 mr-2"/>}
                                {t("delete")}
                              </DropdownMenuItem>)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>);
            })}
              </TableBody>
            </Table>
            </div>

            {/* Pagination */}
            <DataTablePagination
              page={meta.page}
              pageCount={meta.totalPages}
              pageSize={perPage}
              total={meta.total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                  setPerPage(n);
                  setPage(1);
              }}
            />
          </>)}

      {/* Generate Modal */}
      <Modal isOpen={showModal} onClose={() => {
            setShowModal(false);
            setGenTestId('');
            setGenCount(10);
            setGenExpiresAt('');
        }} title={t("generate_access_keys")} description={t("create_new_invite_codes_for_an_assessment")}>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>{t("assessment")}</Label>
            <Select value={genTestId} onValueChange={setGenTestId}>
              <SelectTrigger>
                <SelectValue placeholder={t("select_an_assessment")}/>
              </SelectTrigger>
              <SelectContent>
                {tests.map((t) => (<SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("number_of_codes")}</Label>
            <Input type="number" min={1} max={100} value={genCount} onChange={(e) => setGenCount(Number(e.target.value))} placeholder={t("codes_count_placeholder")}/>
          </div>
          <div className="space-y-2">
            <Label>{t("expiration_date_optional")}</Label>
            <DatePicker value={genExpiresAt} onChange={setGenExpiresAt} min={new Date().toISOString().split('T')[0]} />
          </div>
          <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:justify-end">
            <Button className="w-full sm:w-auto" variant="outline" onClick={() => {
            setShowModal(false);
            setGenTestId('');
            setGenCount(10);
            setGenExpiresAt('');
        }}>{t("cancel")}</Button>
            <Button className="w-full sm:w-auto" onClick={handleGenerate} disabled={generating || !genTestId}>
              {generating && <Loader2 size={14} className="mr-2 animate-spin"/>}
              {t("generate")} {genCount > 0 ? genCount : ''}
              {genCount > 0 ? ' ' : ''}
              {t(genCount === 1 ? "Code" : "Codes")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>);
};
export default InviteManager;
